import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { authApi } from "@/api/endpoints/auth.js";
import { useAuth } from "./auth-context.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Label } from "@/shared/ui/label.jsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card.jsx";
import { toast } from "@/shared/ui/toaster.jsx";
import logo from "@/assets/odontoplug-logo.png";

/**
 * Tela de login — mesmos campos e mesmos textos de botão do app atual.
 *
 * O que mudou por baixo: uma única chamada ao servidor decide tudo. Antes o
 * navegador consultava a tabela `master_users` comparando a senha em texto
 * puro, e só depois tentava o Supabase Auth, com um fallback de e-mail fixo.
 */
export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  /** Volta para a página que o usuário tentou abrir antes de ser barrado. */
  const destinoPretendido = location.state?.from;

  async function handleSubmit(evento) {
    evento.preventDefault();
    setLoading(true);

    try {
      const { user, redirectTo } = await authApi.login({ email: email.trim(), password });
      setSession(user);
      toast.success(user.isPlatform ? "Acesso Master autorizado" : "Acesso autorizado");
      navigate(destinoPretendido ?? redirectTo, { replace: true });
    } catch (erro) {
      toast.error(erro.message ?? "E-mail ou senha inválidos");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <Card className="mx-auto w-full max-w-md border-border/50 shadow-2xl">
        <CardHeader className="flex flex-col items-center space-y-4 text-center">
          <Link to="/">
            <img src={logo} alt="OdontoPlug" className="mb-2 h-10 w-auto dark:brightness-0 dark:invert" />
          </Link>
          <div className="space-y-1">
            <CardTitle className="font-display text-2xl font-bold">Acesse sua conta</CardTitle>
            <CardDescription>Entre com seus dados para acessar o CRM</CardDescription>
          </div>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="exemplo@clinica.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Senha</Label>
                <Button
                  variant="link"
                  className="h-auto p-0 text-xs"
                  type="button"
                  onClick={() =>
                    toast.info("Peça ao administrador da clínica para redefinir sua senha.")
                  }
                >
                  Esqueceu a senha?
                </Button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              disabled={loading}
              className="h-12 w-full rounded-xl text-base font-semibold"
            >
              {loading ? "Entrando..." : "Entrar no sistema"}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              Ainda não tem uma conta?{" "}
              <Button
                variant="link"
                className="h-auto p-0 text-xs"
                type="button"
                onClick={() => toast.info("Fale com a gente em contato@odontoplug.com.br")}
              >
                Solicite uma demonstração
              </Button>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
